import { Button } from "@/components/ui/button";
import { DAYS, FREQUENCIES, nextRunLabel } from "@/lib/schedule";
import type { ScheduleRow } from "./CreateScheduleCard";

type Props = {
  rows: ScheduleRow[];
  onEdit: (s: ScheduleRow) => void;
  onToggle: (s: ScheduleRow) => void;
  onDelete: (s: ScheduleRow) => void;
};

export function SchedulesList({ rows, onEdit, onToggle, onDelete }: Props) {
  if (rows.length === 0)
    return <p className="text-sm text-muted-foreground">No schedules yet. Create one above to put your blog on autopilot.</p>;

  return (
    <div className="space-y-2">
      {rows.map((s) => {
        const freq = FREQUENCIES.find((f) => f.value === s.frequency)?.label ?? s.frequency;
        const dayNames = (s.days_of_week ?? []).map((d) => DAYS.find((x) => x.value === d)?.long).filter(Boolean).join(", ");
        return (
          <div key={s.id} className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className={`rounded px-2 py-0.5 text-xs uppercase tracking-wider ${s.active ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"}`}>
                  {s.active ? "Active" : "Paused"}
                </span>
                <span className="text-xs text-muted-foreground">
                  {s.publish_automatically ? "Publishes automatically" : "Saves as draft"}
                </span>
              </div>
              <div className="font-medium">{s.name}</div>
              <div className="text-xs text-muted-foreground">
                {freq} · {s.frequency === "daily" ? "Every day" : dayNames} · {s.time_of_day?.slice(0, 5)} · {s.timezone} · {s.length_words} words
              </div>
              <div className="mt-1 text-xs">
                <span className="text-muted-foreground">Next run: </span>
                <span className="font-medium">{s.active ? nextRunLabel(s.next_run_at, s.timezone) : "Paused"}</span>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button variant="outline" size="sm" onClick={() => onEdit(s)}>Edit</Button>
              <Button variant="outline" size="sm" onClick={() => onToggle(s)}>{s.active ? "Pause" : "Activate"}</Button>
              <Button variant="ghost" size="sm" onClick={() => onDelete(s)}>Delete</Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
